import type { BookingWindow } from 'lumen/utils/net-revenue/cost/booking-window';
import { buildDefaultBookingWindow } from 'lumen/utils/net-revenue/cost/booking-window';
import type {
  CommissionMode,
  CommissionUnit,
  CostValuesView,
} from 'lumen/utils/net-revenue/cost/types';

/** Structural rather than the EmberData model, so the mapper stays pure. */
export interface CostProfileLike {
  costs: {
    commissionType: CommissionMode;
    costValues: CostValuesView;
    valueType: CommissionUnit;
  }[];
  description: string | null;
  name: string | null;
}

export interface CostProfileFormState {
  bookingWindows: BookingWindow[];
  currencyValue: number | null;
  description: string;
  mode: CommissionMode;
  name: string;
  percentageValue: number | null;
  unit: CommissionUnit;
}

/** Only `costs[0]` and one booking window are mapped — that's all the form can currently produce or render. */
export function profileToFormState(
  profile: CostProfileLike,
): CostProfileFormState {
  const base = {
    name: profile.name ?? '',
    description: profile.description ?? '',
  };

  const cost = profile.costs[0];
  if (!cost) {
    return {
      ...base,
      mode: 'simple',
      unit: 'percentage',
      currencyValue: null,
      percentageValue: null,
      bookingWindows: [buildDefaultBookingWindow()],
    };
  }

  const { commissionType: mode, valueType: unit, costValues } = cost;

  if (mode === 'simple') {
    return {
      ...base,
      mode,
      unit,
      currencyValue: unit === 'currency' ? costValues.value : null,
      percentageValue: unit === 'percentage' ? costValues.value : null,
      bookingWindows: [buildDefaultBookingWindow()],
    };
  }

  const savedBookingWindow = costValues.bookingWindows[0];
  const bookingWindow = buildDefaultBookingWindow();
  if (savedBookingWindow) {
    bookingWindow.leadtimeStart = savedBookingWindow.leadtimeStart;
    bookingWindow.leadtimeEnd = savedBookingWindow.leadtimeEnd;
    if (unit === 'currency') {
      bookingWindow.currencyValues = savedBookingWindow.values;
    } else {
      bookingWindow.percentageValues = savedBookingWindow.values;
    }
  }

  return {
    ...base,
    mode,
    unit,
    currencyValue: null,
    percentageValue: null,
    bookingWindows: [bookingWindow],
  };
}
